import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { CookieManager } from '../../utils/cookies';

// Theme types
export type ThemeMode = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

// Glass theme variants
export interface GlassThemeConfig {
	mode: ThemeMode;
	resolvedTheme: ResolvedTheme;
	setTheme: (theme: ThemeMode) => void;
	toggleTheme: () => void;
}

// Theme colors are now integrated directly into glassVariants for better maintainability

// Glass component variants
export const glassVariants = {
	// Background variants - Beautiful frosted glass slab with visible texture in light mode
	background: {
		light: 'bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100/95 backdrop-blur-md relative before:content-[""] before:absolute before:inset-0 before:bg-[radial-gradient(circle_at_30%_20%,rgba(120,119,198,0.2),transparent_50%),radial-gradient(circle_at_70%_80%,rgba(99,102,241,0.2),transparent_50%),linear-gradient(135deg,transparent_0%,rgba(255,255,255,0.4)_50%,transparent_100%)] before:pointer-events-none after:content-[""] after:absolute after:inset-0 after:bg-[url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%239C92AC\' fill-opacity=\'0.04\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")] after:opacity-40 after:pointer-events-none',
		dark: 'bg-gradient-to-br from-gray-900/95 via-gray-800/90 to-blue-900/95 backdrop-blur-sm relative before:content-[""] before:absolute before:inset-0 before:bg-[radial-gradient(circle_at_30%_20%,rgba(99,102,241,0.15),transparent_50%),radial-gradient(circle_at_70%_80%,rgba(139,92,246,0.15),transparent_50%)] before:pointer-events-none',
	},

	/** Home fixed backdrop — ~10% frost so video/orbs stay visible (does not scroll). */
	backgroundFixed: {
		light:
			'bg-gradient-to-br from-slate-100/10 via-blue-50/10 to-indigo-100/10 backdrop-blur-sm fixed inset-0 z-0 overflow-hidden pointer-events-none before:content-[""] before:absolute before:inset-0 before:bg-[radial-gradient(circle_at_30%_20%,rgba(120,119,198,0.02),transparent_50%),radial-gradient(circle_at_70%_80%,rgba(99,102,241,0.02),transparent_50%),linear-gradient(135deg,transparent_0%,rgba(255,255,255,0.04)_50%,transparent_100%)] before:pointer-events-none after:content-[""] after:absolute after:inset-0 after:bg-[url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%239C92AC\' fill-opacity=\'0.04\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")] after:opacity-10 after:pointer-events-none',
		dark:
			'bg-gradient-to-br from-gray-900/10 via-gray-800/10 to-blue-900/10 backdrop-blur-sm fixed inset-0 z-0 overflow-hidden pointer-events-none before:content-[""] before:absolute before:inset-0 before:bg-[radial-gradient(circle_at_30%_20%,rgba(99,102,241,0.02),transparent_50%),radial-gradient(circle_at_70%_80%,rgba(139,92,246,0.02),transparent_50%)] before:pointer-events-none',
	},

	/** MainLayout on home (`/`) — no scrolling page gradient; Welcome provides fixed viewport bg. */
	layoutShellHome: {
		light: 'bg-transparent',
		dark: 'bg-transparent',
	},

	// Card variants (main containers) - Elevated frosted glass tile with beautiful depth
	// NOTE: before:/after: use rounded-[inherit] (not a fixed rounded-3xl) so
	// the decorative overlay always matches whatever radius the consumer
	// applies to the element itself (e.g. `${theme.card} rounded-lg`) -
	// otherwise the overlay keeps this token's default radius while the box
	// takes on the override, producing mismatched "double edge" corners.
	card: {
		light: 'backdrop-blur-xl bg-white/40 shadow-2xl shadow-gray-500/20 border border-white/50 rounded-3xl relative before:content-[""] before:absolute before:inset-0 before:bg-gradient-to-br before:from-white/30 before:via-white/10 before:to-transparent before:pointer-events-none before:rounded-[inherit] after:content-[""] after:absolute after:inset-0 after:bg-[linear-gradient(135deg,transparent_0%,rgba(255,255,255,0.1)_50%,transparent_100%)] after:pointer-events-none after:rounded-[inherit]',
		dark: 'backdrop-blur-xl bg-gray-900/40 shadow-2xl shadow-black/40 border border-gray-700/40 rounded-3xl relative before:content-[""] before:absolute before:inset-0 before:bg-gradient-to-br before:from-white/15 before:via-white/5 before:to-transparent before:pointer-events-none before:rounded-[inherit] after:content-[""] after:absolute after:inset-0 after:bg-[linear-gradient(135deg,transparent_0%,rgba(255,255,255,0.05)_50%,transparent_100%)] after:pointer-events-none after:rounded-[inherit]',
	},

	// Page wrapper - Main body glass slab container
	pageWrapper: {
		light: 'relative min-h-[calc(100vh-200px)] rounded-3xl backdrop-blur-2xl bg-white/10 shadow-2xl border border-white/20 before:content-[""] before:absolute before:inset-0 before:bg-gradient-to-br before:from-white/15 before:via-white/8 before:to-transparent before:pointer-events-none before:rounded-[inherit] after:content-[""] after:absolute after:inset-0 after:bg-[linear-gradient(135deg,transparent_0%,rgba(255,255,255,0.08)_50%,transparent_100%)] after:pointer-events-none after:rounded-[inherit]',
		dark: 'relative min-h-[calc(100vh-200px)] rounded-3xl backdrop-blur-2xl bg-gray-900/10 shadow-2xl border border-gray-700/20 before:content-[""] before:absolute before:inset-0 before:bg-gradient-to-br before:from-white/8 before:via-white/4 before:to-transparent before:pointer-events-none before:rounded-[inherit] after:content-[""] after:absolute after:inset-0 after:bg-[linear-gradient(135deg,transparent_0%,rgba(255,255,255,0.04)_50%,transparent_100%)] after:pointer-events-none after:rounded-[inherit]',
	},

	// Header variants - Enhanced crystal glass effect with stronger backdrop blur
	header: {
		light: 'backdrop-blur-xl bg-white/15 shadow-lg shadow-gray-500/10 border-b border-white/25',
		dark: 'backdrop-blur-xl bg-gray-900/85 shadow-lg shadow-black/20 border-b border-gray-700/30',
	},

	// Footer variants - Enhanced crystal glass effect
	footer: {
		light: 'backdrop-blur-md bg-white/6 shadow-lg shadow-gray-500/6 border-t border-white/12',
		dark: 'backdrop-blur-md bg-gray-900/80 shadow-lg shadow-black/12 border-t border-gray-700/20',
	},

	// Input variants
	input: {
		light: 'backdrop-blur-sm bg-white/80 shadow-lg focus:bg-white/95 focus:shadow-xl focus:shadow-blue-500/10',
		dark: 'backdrop-blur-sm bg-gray-800/80 shadow-lg focus:bg-gray-800/95 focus:shadow-xl focus:shadow-blue-400/10',
		error: {
			light: 'ring-2 ring-red-500/60 bg-red-50/60 shadow-lg shadow-red-500/10',
			dark: 'ring-2 ring-red-400/60 bg-red-900/40 shadow-lg shadow-red-400/10',
		},
	},

	// Button variants - Enhanced crystal glass with elegant animations
	button: {
		primary: {
			light: 'backdrop-blur-sm bg-gradient-to-r from-blue-500/90 to-blue-600/90 hover:from-blue-600/95 hover:to-blue-700/95 text-white font-semibold shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/40 border border-blue-300/30 transition-all duration-300 glass-ripple',
			dark: 'backdrop-blur-sm bg-gradient-to-r from-blue-400/80 to-blue-500/80 hover:from-blue-500/90 hover:to-blue-600/90 text-white font-semibold shadow-lg shadow-blue-400/25 hover:shadow-xl hover:shadow-blue-400/40 border border-blue-300/20 transition-all duration-300 glass-ripple',
		},
		secondary: {
			light: 'backdrop-blur-xl bg-white/8 hover:bg-white/25 text-gray-900 font-semibold shadow-lg shadow-gray-300/10 hover:shadow-xl hover:shadow-gray-300/20 border border-white/15 hover:border-white/40 transition-all duration-300 elegant-hover',
			dark: 'backdrop-blur-xl bg-gray-800/15 hover:bg-gray-700/35 text-white font-semibold shadow-lg shadow-gray-800/15 hover:shadow-xl hover:shadow-gray-700/25 border border-gray-600/15 hover:border-gray-500/35 transition-all duration-300 elegant-hover',
		},
		ghost: {
			light: 'backdrop-blur-lg bg-transparent hover:bg-white/20 text-gray-800 hover:text-gray-900 shadow-none hover:shadow-lg hover:shadow-gray-300/15 border border-transparent hover:border-white/25 transition-all duration-300 smooth-focus',
			dark: 'backdrop-blur-lg bg-transparent hover:bg-gray-800/25 text-gray-100 hover:text-white shadow-none hover:shadow-lg hover:shadow-gray-700/20 border border-transparent hover:border-gray-600/25 transition-all duration-300 smooth-focus',
		},
		disabled: {
			light: 'backdrop-blur-sm bg-gray-200/60 cursor-not-allowed opacity-50 font-semibold shadow-sm text-gray-500 border border-gray-300/30',
			dark: 'backdrop-blur-sm bg-gray-700/60 cursor-not-allowed opacity-50 font-semibold shadow-sm text-gray-400 border border-gray-600/30',
		},
	},

	// Add to glassVariants
	checkbox: {
		light: 'backdrop-blur-sm bg-white/80 border border-gray-300/50 checked:bg-blue-600 checked:border-blue-600 shadow-sm',
		dark: 'backdrop-blur-sm bg-gray-800/80 border border-gray-600/50 checked:bg-blue-500 checked:border-blue-500 shadow-sm',
	},
	radio: {
		light: 'backdrop-blur-sm bg-white/80 border border-gray-300/50 checked:bg-blue-600 checked:border-blue-600 shadow-sm',
		dark: 'backdrop-blur-sm bg-gray-800/80 border border-gray-600/50 checked:bg-blue-500 checked:border-blue-500 shadow-sm',
	},
	toggle: {
		light: 'backdrop-blur-sm bg-gray-200/80 checked:bg-blue-600 shadow-sm border border-gray-300/50',
		dark: 'backdrop-blur-sm bg-gray-700/80 checked:bg-blue-500 shadow-sm border border-gray-600/50',
	},
	select: {
		light: 'backdrop-blur-sm bg-white/80 border border-gray-300/50 shadow-lg focus:bg-white/95 focus:shadow-xl focus:shadow-blue-500/10',
		dark: 'backdrop-blur-sm bg-gray-800/80 border border-gray-600/50 shadow-lg focus:bg-gray-800/95 focus:shadow-xl focus:shadow-blue-400/10',
	},
	textarea: {
		light: 'backdrop-blur-sm bg-white/80 border border-gray-300/50 shadow-lg focus:bg-white/95 focus:shadow-xl focus:shadow-blue-500/10 resize-none',
		dark: 'backdrop-blur-sm bg-gray-800/80 border border-gray-600/50 shadow-lg focus:bg-gray-800/95 focus:shadow-xl focus:shadow-blue-400/10 resize-none',
	},
	// Enhanced crystal glass sections and containers
	section: {
		light: 'backdrop-blur-lg bg-white/15 shadow-lg shadow-gray-500/6 rounded-2xl border border-white/25',
		dark: 'backdrop-blur-lg bg-white/8 shadow-lg shadow-black/20 rounded-2xl border border-white/15',
	},
	container: {
		light: 'backdrop-blur-xl bg-white/20 shadow-xl shadow-gray-500/8 border border-white/30',
		dark: 'backdrop-blur-xl bg-white/10 shadow-xl shadow-black/25 border border-white/20',
	},
	panel: {
		light: 'backdrop-blur-xl bg-white/25 shadow-2xl shadow-gray-500/10 border border-white/35',
		dark: 'backdrop-blur-xl bg-white/12 shadow-2xl shadow-black/30 border border-white/25',
	},
	slider: {
		light: 'backdrop-blur-sm bg-white/80 shadow-lg focus:bg-white/95 focus:shadow-xl focus:shadow-blue-500/10',
		dark: 'backdrop-blur-sm bg-gray-800/80 shadow-lg focus:bg-gray-800/95 focus:shadow-xl focus:shadow-blue-400/10',
	},
	/** Native `<input type="range">` — glass track + primary-gradient thumb (matches `button.primary` / inputs); no thumb transitions. */
	rangeSlider: {
		light:
			'w-full h-2.5 rounded-full appearance-none cursor-pointer touch-none bg-transparent ' +
			'focus:outline-none focus:ring-2 focus:ring-blue-500/35 focus:ring-offset-2 focus:ring-offset-slate-100/80 ' +
			'accent-indigo-600 [&:hover]:accent-blue-600 [&:active]:accent-indigo-700 ' +
			'[&::-webkit-slider-runnable-track]:h-2.5 [&::-webkit-slider-runnable-track]:rounded-full ' +
			'[&::-webkit-slider-runnable-track]:backdrop-blur-sm [&::-webkit-slider-runnable-track]:bg-white/55 ' +
			'[&::-webkit-slider-runnable-track]:border [&::-webkit-slider-runnable-track]:border-white/45 ' +
			'[&::-webkit-slider-runnable-track]:shadow-[inset_0_1px_3px_rgba(0,0,0,0.06)] ' +
			'[&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:rounded-full ' +
			'[&::-webkit-slider-thumb]:bg-gradient-to-br [&::-webkit-slider-thumb]:from-blue-500 [&::-webkit-slider-thumb]:to-indigo-600 ' +
			'[&::-webkit-slider-thumb]:shadow-lg [&::-webkit-slider-thumb]:shadow-blue-500/30 [&::-webkit-slider-thumb]:ring-2 [&::-webkit-slider-thumb]:ring-white/70 [&::-webkit-slider-thumb]:border-0 ' +
			'[&::-moz-range-track]:h-2.5 [&::-moz-range-track]:rounded-full [&::-moz-range-track]:backdrop-blur-sm [&::-moz-range-track]:bg-white/55 ' +
			'[&::-moz-range-track]:border [&::-moz-range-track]:border-white/45 [&::-moz-range-track]:shadow-[inset_0_1px_3px_rgba(0,0,0,0.06)] ' +
			'[&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:rounded-full ' +
			'[&::-moz-range-thumb]:bg-gradient-to-br [&::-moz-range-thumb]:from-blue-500 [&::-moz-range-thumb]:to-indigo-600 ' +
			'[&::-moz-range-thumb]:shadow-lg [&::-moz-range-thumb]:shadow-blue-500/30 [&::-moz-range-thumb]:ring-2 [&::-moz-range-thumb]:ring-white/70 [&::-moz-range-thumb]:border-0',
		dark:
			'w-full h-2.5 rounded-full appearance-none cursor-pointer touch-none bg-transparent ' +
			'focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-2 focus:ring-offset-gray-900/80 ' +
			'accent-blue-400 [&:hover]:accent-indigo-300 [&:active]:accent-indigo-200 ' +
			'[&::-webkit-slider-runnable-track]:h-2.5 [&::-webkit-slider-runnable-track]:rounded-full ' +
			'[&::-webkit-slider-runnable-track]:backdrop-blur-sm [&::-webkit-slider-runnable-track]:bg-gray-800/65 ' +
			'[&::-webkit-slider-runnable-track]:border [&::-webkit-slider-runnable-track]:border-gray-600/45 ' +
			'[&::-webkit-slider-runnable-track]:shadow-[inset_0_1px_3px_rgba(0,0,0,0.25)] ' +
			'[&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:rounded-full ' +
			'[&::-webkit-slider-thumb]:bg-gradient-to-br [&::-webkit-slider-thumb]:from-blue-400 [&::-webkit-slider-thumb]:to-indigo-500 ' +
			'[&::-webkit-slider-thumb]:shadow-lg [&::-webkit-slider-thumb]:shadow-indigo-500/25 [&::-webkit-slider-thumb]:ring-2 [&::-webkit-slider-thumb]:ring-white/15 [&::-webkit-slider-thumb]:border-0 ' +
			'[&::-moz-range-track]:h-2.5 [&::-moz-range-track]:rounded-full [&::-moz-range-track]:backdrop-blur-sm [&::-moz-range-track]:bg-gray-800/65 ' +
			'[&::-moz-range-track]:border [&::-moz-range-track]:border-gray-600/45 [&::-moz-range-track]:shadow-[inset_0_1px_3px_rgba(0,0,0,0.25)] ' +
			'[&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:rounded-full ' +
			'[&::-moz-range-thumb]:bg-gradient-to-br [&::-moz-range-thumb]:from-blue-400 [&::-moz-range-thumb]:to-indigo-500 ' +
			'[&::-moz-range-thumb]:shadow-lg [&::-moz-range-thumb]:shadow-indigo-500/25 [&::-moz-range-thumb]:ring-2 [&::-moz-range-thumb]:ring-white/15 [&::-moz-range-thumb]:border-0',
	},
	// Add to glassVariants
	progressBar: {
		track: {
			light: 'backdrop-blur-sm bg-gray-200/60 shadow-inner',
			dark: 'backdrop-blur-sm bg-gray-700/60 shadow-inner',
		},
		fill: {
			light: 'bg-blue-600 shadow-lg shadow-blue-600/25',
			dark: 'bg-blue-500 shadow-lg shadow-blue-500/25',
		},
	},
	skeleton: {
		light: 'backdrop-blur-sm bg-gray-200/60 animate-pulse',
		dark: 'backdrop-blur-sm bg-gray-700/60 animate-pulse',
	},
	spinner: {
		light: 'border-gray-200/60 border-t-blue-600',
		dark: 'border-gray-700/60 border-t-blue-500',
	},

	// Loading spinner variants for different contexts
	loadingSpinner: {
		small: {
			light: 'w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin',
			dark: 'w-4 h-4 border-2 border-gray-300/30 border-t-gray-300 rounded-full animate-spin',
		},
		medium: {
			light: 'w-8 h-8 border-b-2 border-blue-500 rounded-full animate-spin',
			dark: 'w-8 h-8 border-b-2 border-blue-400 rounded-full animate-spin',
		},
		large: {
			light: 'w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin',
			dark: 'w-12 h-12 border-4 border-blue-800 border-t-blue-400 rounded-full animate-spin',
		},
	},

	// Warning alert variant (missing from alerts)
	warningAlert: {
		light: 'backdrop-blur-md bg-orange-50/90 shadow-lg shadow-orange-500/10 text-orange-800 border border-orange-200/50',
		dark: 'backdrop-blur-md bg-orange-900/50 shadow-lg shadow-orange-400/10 text-orange-200 border border-orange-800/50',
	},

	// Warning text variants
	warningText: {
		light: 'text-orange-600',
		dark: 'text-orange-400',
	},

	// Modal overlay with proper backdrop
	modalBackdrop: {
		light: 'fixed inset-0 backdrop-blur-sm bg-black/20 z-50',
		dark: 'fixed inset-0 backdrop-blur-sm bg-black/40 z-50',
	},

	// Error boundary variants for development and production
	errorBoundary: {
		container: {
			light: 'min-h-screen flex items-center justify-center p-4',
			dark: 'min-h-screen flex items-center justify-center p-4',
		},
		card: {
			light: 'backdrop-blur-xl bg-white/95 shadow-2xl rounded-2xl p-8 max-w-lg w-full border border-red-200/50',
			dark: 'backdrop-blur-xl bg-gray-900/95 shadow-2xl rounded-2xl p-8 max-w-lg w-full border border-red-700/50',
		},
		background: {
			light: 'bg-red-50/80',
			dark: 'bg-red-900/20',
		},
	},

	// Image border variants
	imageBorder: {
		light: 'border border-gray-200/60 rounded-lg',
		dark: 'border border-gray-600/60 rounded-lg',
	},

	/** Media Master — placeholder / loading frames + home hero backdrop video */
	media: {
		pendingFrame: {
			light: 'min-h-32 w-full rounded-xl animate-pulse',
			dark: 'min-h-32 w-full rounded-xl animate-pulse',
		},
		loadingBox: {
			light: 'flex min-h-32 w-full items-center justify-center rounded-xl text-sm',
			dark: 'flex min-h-32 w-full items-center justify-center rounded-xl text-sm',
		},
		/** Full-viewport layer; stays fixed while page scrolls (home hero). */
		heroBackdrop: {
			light:
				'pointer-events-none fixed inset-0 z-0 h-[100dvh] min-h-[100dvh] w-full overflow-hidden',
			dark:
				'pointer-events-none fixed inset-0 z-0 h-[100dvh] min-h-[100dvh] w-full overflow-hidden',
		},
		/** Fills the fixed backdrop edge-to-edge; object-cover = full window without letterboxing (AR preserved). */
		heroBackdropVideo: {
			light:
				'pointer-events-none absolute inset-0 z-0 block h-full w-full min-h-full min-w-full object-cover object-center',
			dark:
				'pointer-events-none absolute inset-0 z-0 block h-full w-full min-h-full min-w-full object-cover object-center',
		},
		/** Frost over hero video, heavier in the text-bearing middle - keeps
		 * footage visible at the edges while guaranteeing contrast for the
		 * dark-on-light / light-on-dark hero text regardless of what's
		 * playing underneath. Light mode needs much more than a 10% wash:
		 * dark hero text over a bright/high-detail video was unreadable. */
		heroBackdropScrim: {
			light: 'pointer-events-none absolute inset-0 z-[1] bg-gradient-to-b from-white/55 via-white/70 to-white/55',
			dark: 'pointer-events-none absolute inset-0 z-[1] bg-gradient-to-b from-black/35 via-black/50 to-black/35',
		},
		heroOrbLayer: {
			light: 'fixed inset-0 overflow-hidden pointer-events-none z-[1]',
			dark: 'fixed inset-0 overflow-hidden pointer-events-none z-[1]',
		},
		heroContentLayer: {
			light: 'relative z-10',
			dark: 'relative z-10',
		},
	},

	// Enhanced placeholder text (theme-aware)
	placeholderText: {
		light: 'placeholder:text-gray-400',
		dark: 'placeholder:text-gray-500',
	},

	// Theme toggle hover effects (proper theme-aware)
	themeToggleHover: {
		light: 'hover:bg-white/40 hover:shadow-md hover:shadow-blue-500/10 dark:hover:bg-gray-800/40',
		dark: 'hover:bg-gray-800/40 hover:shadow-md hover:shadow-blue-400/10',
	},

	// Add to glassVariants
	tooltip: {
		light: 'backdrop-blur-md bg-gray-900/90 text-white shadow-lg shadow-black/20 text-sm rounded-lg',
		dark: 'backdrop-blur-md bg-gray-100/90 text-gray-900 shadow-lg shadow-white/20 text-sm rounded-lg',
	},
	popover: {
		light: 'backdrop-blur-xl bg-white/95 shadow-2xl shadow-gray-500/10 border border-white/20 rounded-xl',
		dark: 'backdrop-blur-xl bg-gray-900/95 shadow-2xl shadow-black/20 border border-gray-700/30 rounded-xl',
	},
	drawer: {
		light: 'backdrop-blur-xl bg-white/95 shadow-2xl shadow-gray-500/10',
		dark: 'backdrop-blur-xl bg-gray-900/95 shadow-2xl shadow-black/20',
	},

	// Add to glassVariants
	table: {
		light: 'backdrop-blur-lg bg-white/85 shadow-xl shadow-gray-500/5 border border-white/20',
		dark: 'backdrop-blur-lg bg-gray-900/85 shadow-xl shadow-black/15 border border-gray-700/30',
	},
	tableHeader: {
		light: 'backdrop-blur-md bg-gray-50/60 border-b border-gray-200/50',
		dark: 'backdrop-blur-md bg-gray-800/60 border-b border-gray-700/50',
	},
	tableRow: {
		light: 'hover:bg-white/50 border-b border-gray-200/30 text-gray-900',
		dark: 'hover:bg-gray-800/50 border-b border-gray-700/30 text-gray-100',
	},
	list: {
		light: 'backdrop-blur-lg bg-white/85 shadow-lg shadow-gray-500/5 rounded-xl',
		dark: 'backdrop-blur-lg bg-gray-900/85 shadow-lg shadow-black/15 rounded-xl',
	},
	listItem: {
		light: 'hover:bg-white/40 border-b border-gray-200/30 last:border-b-0 text-gray-900 hover:text-gray-900',
		dark: 'hover:bg-gray-800/40 border-b border-gray-700/30 last:border-b-0 text-gray-100 hover:text-gray-100',
	},
	// Text variants with better contrast
	text: {
		primary: {
			light: 'text-gray-900',
			dark: 'text-gray-100',
		},
		secondary: {
			light: 'text-gray-700',
			dark: 'text-gray-300',
		},
		muted: {
			light: 'text-gray-500',
			dark: 'text-gray-400',
		},
		label: {
			light: 'text-gray-800 font-medium',
			dark: 'text-gray-200 font-medium',
		},
		placeholder: {
			light: 'placeholder-gray-400',
			dark: 'placeholder-gray-500',
		},
		heading: {
			light: 'text-gray-900 font-bold',
			dark: 'text-gray-100 font-bold',
		},
		accent: {
			light: 'text-blue-600 font-semibold',
			dark: 'text-blue-400 font-semibold',
		},
		error: {
			light: 'text-red-600',
			dark: 'text-red-400',
		},
		errorHeading: {
			light: 'text-red-800 font-semibold',
			dark: 'text-red-300 font-semibold',
		},
		success: {
			light: 'text-green-600',
			dark: 'text-green-400',
		},
		info: {
			light: 'text-blue-600',
			dark: 'text-blue-400',
		},
		infoHeading: {
			light: 'text-blue-800 font-semibold',
			dark: 'text-blue-300 font-semibold',
		},
		strikethrough: {
			light: 'line-through decoration-gray-900/80',
			dark: 'line-through decoration-gray-100/80',
		},
		multiline: {
			light: 'whitespace-pre-line',
			dark: 'whitespace-pre-line',
		},
	},

	// Button text variants - Crystal clear text with enhanced contrast
	buttonText: {
		primary: {
			light: 'text-white font-semibold drop-shadow-sm',
			dark: 'text-white font-semibold drop-shadow-sm',
		},
		secondary: {
			light: 'text-gray-900 font-semibold drop-shadow-sm',
			dark: 'text-white font-semibold drop-shadow-sm',
		},
		ghost: {
			light: 'text-gray-800 hover:text-gray-900 font-medium drop-shadow-sm',
			dark: 'text-gray-100 hover:text-white font-medium drop-shadow-sm',
		},
		disabled: {
			light: 'text-gray-500 font-medium',
			dark: 'text-gray-400 font-medium',
		},
	},

	// Badge variants
	badge: {
		light: 'bg-gray-100 text-gray-900 border border-gray-200',
		dark: 'bg-gray-800 text-gray-100 border border-gray-700',
	},

	dashboardSettings: {
		toggleTrackOn: {
			light: 'bg-gradient-to-r from-blue-500 to-blue-600 shadow-md hover:shadow-lg',
			dark: 'bg-gradient-to-r from-blue-400 to-blue-500 shadow-md hover:shadow-lg',
		},
		toggleTrackOff: {
			light: 'bg-gray-300/80 backdrop-blur-sm',
			dark: 'bg-gray-600/80 backdrop-blur-sm',
		},
		toggleKnob: {
			light: 'bg-white shadow-lg',
			dark: 'bg-white shadow-lg',
		},
		toggleFocusRing: {
			light: 'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500/50',
			dark: 'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-400/50',
		},
		radioCardSelected: {
			light:
				'bg-gradient-to-br from-blue-50/50 to-blue-100/30 border-blue-500/50 shadow-lg scale-[1.02] border-2',
			dark:
				'bg-gradient-to-br from-blue-900/30 to-blue-800/20 border-blue-500/50 shadow-lg scale-[1.02] border-2',
		},
		radioCardUnselected: {
			light: 'border-2 border-transparent hover:border-blue-300/30 hover:shadow-md hover:scale-[1.01]',
			dark: 'border-2 border-transparent hover:border-blue-600/30 hover:shadow-md hover:scale-[1.01]',
		},
		radioOuterChecked: {
			light: 'border-blue-600 bg-blue-600 shadow-md border-2',
			dark: 'border-blue-400 bg-blue-400 shadow-md border-2',
		},
		radioOuterUnchecked: {
			light: 'border-2 border-gray-300 group-hover:border-blue-400',
			dark: 'border-2 border-gray-600 group-hover:border-blue-500',
		},
		radioInnerDot: {
			light: 'bg-white shadow-sm',
			dark: 'bg-white shadow-sm',
		},
		passwordInputFocus: {
			light: 'focus:scale-[1.01] focus:ring-2 focus:ring-blue-500/50',
			dark: 'focus:scale-[1.01] focus:ring-2 focus:ring-blue-400/50',
		},
		passwordMeterTrack: {
			light: 'backdrop-blur-sm bg-gray-200/80',
			dark: 'backdrop-blur-sm bg-gray-700/80',
		},
		passwordMeterWeak: {
			light: 'bg-red-500',
			dark: 'bg-red-500',
		},
		passwordMeterMedium: {
			light: 'bg-yellow-500',
			dark: 'bg-yellow-500',
		},
		passwordMeterStrong: {
			light: 'bg-green-500',
			dark: 'bg-green-500',
		},
	},

	// Enhanced crystal glass form
	form: {
		light: 'backdrop-blur-xl bg-white/20 shadow-xl rounded-2xl shadow-gray-500/8 border border-white/30',
		dark: 'backdrop-blur-xl bg-white/8 shadow-xl rounded-2xl shadow-black/25 border border-white/20',
	},

	// Enhanced modal variants
	modal: {
		overlay: {
			light: 'backdrop-blur-sm bg-black/20',
			dark: 'backdrop-blur-sm bg-black/40',
		},
		content: {
			light:
				'backdrop-blur-3xl bg-white/38 shadow-2xl rounded-3xl shadow-gray-500/12 border border-white/55',
			dark: 'backdrop-blur-xl bg-black/25 shadow-2xl rounded-3xl shadow-black/30 border border-white/12',
		},
	},

	// Alert variants
	alert: {
		info: {
			light: 'backdrop-blur-md bg-blue-50/90 shadow-lg shadow-blue-500/10 text-blue-800',
			dark: 'backdrop-blur-md bg-blue-900/50 shadow-lg shadow-blue-400/10 text-blue-200',
		},
		success: {
			light: 'backdrop-blur-md bg-green-50/90 shadow-lg shadow-green-500/10 text-green-800',
			dark: 'backdrop-blur-md bg-green-900/50 shadow-lg shadow-green-400/10 text-green-200',
		},
		warning: {
			light: 'backdrop-blur-md bg-yellow-50/90 shadow-lg shadow-yellow-500/10 text-yellow-800',
			dark: 'backdrop-blur-md bg-yellow-900/50 shadow-lg shadow-yellow-400/10 text-yellow-200',
		},
		error: {
			light: 'backdrop-blur-md bg-red-50/90 shadow-lg shadow-red-500/10 text-red-800',
			dark: 'backdrop-blur-md bg-red-900/50 shadow-lg shadow-red-400/10 text-red-200',
		},
	},

	// Floating orbs (background decorations)
	orbs: {
		light: ['bg-gradient-to-br from-blue-500/20 to-purple-500/20', 'bg-gradient-to-tr from-teal-500/20 to-blue-500/20'],
		dark: ['bg-gradient-to-br from-blue-500/30 to-purple-500/30', 'bg-gradient-to-tr from-teal-500/30 to-blue-500/30'],
	},

	// Divider variants
	divider: {
		light: 'shadow-sm shadow-gray-400/10 bg-gradient-to-r from-transparent via-gray-300/10 to-transparent h-px',
		dark: 'shadow-sm shadow-gray-600/15 bg-gradient-to-r from-transparent via-gray-600/15 to-transparent h-px',
	},

	// Divider text background
	dividerText: {
		light: 'bg-white/95 text-gray-600 shadow-sm shadow-gray-400/5',
		dark: 'bg-gray-900/95 text-gray-400 shadow-sm shadow-gray-600/10',
	},

	// Sidebar variants
	sidebar: {
		light: 'backdrop-blur-lg bg-white/85 shadow-xl shadow-gray-500/5',
		dark: 'backdrop-blur-lg bg-gray-900/80 shadow-xl shadow-black/15 border border-gray-700/20',
	},

	// Dropdown variants
	dropdown: {
		light: 'backdrop-blur-xl bg-white/95 shadow-2xl rounded-xl shadow-gray-500/10',
		dark: 'backdrop-blur-xl bg-gray-900/95 shadow-2xl rounded-xl shadow-black/20',
	},

	// Avatar variants
	avatar: {
		light: 'backdrop-blur-sm bg-white/60 shadow-lg shadow-gray-400/10 hover:shadow-xl hover:shadow-gray-400/15',
		dark: 'backdrop-blur-sm bg-gray-800/60 shadow-lg shadow-gray-600/10 hover:shadow-xl hover:shadow-gray-600/15',
	},

	// Separator variants (for dropdown items)
	separator: {
		light: 'shadow-sm shadow-gray-400/5 bg-gradient-to-r from-transparent via-gray-300/30 to-transparent h-px my-2',
		dark: 'shadow-sm shadow-gray-600/10 bg-gradient-to-r from-transparent via-gray-600/30 to-transparent h-px my-2',
	},

	// Enhanced navigation variants - Crystal clear transparent
	nav: {
		main: {
			light: 'backdrop-blur-lg bg-white/30 shadow-xl shadow-gray-500/6 border-b border-white/8',
			dark: 'backdrop-blur-lg bg-gray-900/80 shadow-xl shadow-black/20 border-b border-gray-700/20',
		},
		item: {
			light: 'hover:bg-white/15 hover:shadow-md hover:shadow-gray-400/12 transition-all duration-200 text-gray-900 hover:text-gray-900 backdrop-blur-sm rounded-lg',
			dark: 'hover:bg-white/10 hover:shadow-md hover:shadow-gray-600/15 transition-all duration-200 text-gray-100 hover:text-gray-100 backdrop-blur-sm rounded-lg',
		},
		itemActive: {
			light: 'bg-blue-50/30 shadow-md shadow-blue-500/25 text-gray-900 font-semibold backdrop-blur-lg rounded-lg border border-blue-200/40 transform scale-105',
			dark: 'bg-blue-900/40 shadow-md shadow-blue-400/25 text-gray-100 font-semibold backdrop-blur-lg rounded-lg border border-blue-600/40 transform scale-105',
		},
		// Nearly opaque everywhere (not just "mobile") - a 15-25% translucent
		// dropdown over a busy page background was unreadable. This is the
		// single dropdown treatment used by every nav menu, desktop and mobile,
		// so there's one consistent surface instead of two divergent ones
		// (the old separate "dropdownMobile" token wasn't even reliably wired
		// up - `navDropdownMobile` was never mapped in the exported theme
		// object, so it silently resolved to `undefined`).
		dropdown: {
			light: 'backdrop-blur-xl bg-white/95 shadow-2xl shadow-gray-500/12 border border-white/50 rounded-xl',
			dark: 'backdrop-blur-xl bg-gray-900/95 shadow-2xl shadow-black/25 border border-gray-700/40 rounded-xl',
		},
		funToolsChevron: {
			light: 'w-4 h-4 shrink-0 transition-transform duration-200',
			dark: 'w-4 h-4 shrink-0 transition-transform duration-200',
		},
		funToolsChevronOpen: {
			light: 'rotate-180',
			dark: 'rotate-180',
		},
	},

	// Avatar specific variants
	avatarButton: {
		light:
			'backdrop-blur-sm bg-blue-100/20 hover:bg-blue-100/30 shadow-lg shadow-blue-500/15 hover:shadow-xl hover:shadow-blue-500/20',
		dark: 'backdrop-blur-sm bg-blue-900/25 hover:bg-blue-900/35 shadow-lg shadow-blue-400/15 hover:shadow-xl hover:shadow-blue-400/20',
	},

	// Filter variants for project pages
	filter: {
		light: 'backdrop-blur-sm bg-white/80 hover:bg-white/95 text-gray-900 hover:text-gray-900 border border-gray-300/50 shadow-sm hover:shadow-md',
		dark: 'backdrop-blur-sm bg-gray-800/80 hover:bg-gray-800/95 text-gray-100 hover:text-gray-100 border border-gray-600/50 shadow-sm hover:shadow-md',
	},
	filterActive: {
		light: 'backdrop-blur-sm bg-blue-50/10 text-gray-900 font-semibold border border-blue-300/30 shadow-md',
		dark: 'backdrop-blur-sm bg-blue-900/20 text-gray-100 font-semibold border border-blue-600/40 shadow-md',
	},

	// Additional button variants for better contrast
	buttonAccent: {
		light: 'text-blue-600 hover:text-blue-700 font-semibold',
		dark: 'text-blue-400 hover:text-blue-300 font-semibold',
	},

	// Link variants - Enhanced for button-like behavior
	link: {
		light: 'text-blue-600 hover:text-blue-700 font-medium underline decoration-blue-600/30 hover:decoration-blue-600/60',
		dark: 'text-blue-400 hover:text-blue-300 font-medium underline decoration-blue-400/30 hover:decoration-blue-400/60',
	},

	// Button-like interactive elements (links, spans, divs that act as buttons)
	buttonLike: {
		primary: {
			light: 'backdrop-blur-xl bg-blue-600/95 hover:bg-blue-700 text-white font-semibold shadow-lg shadow-blue-600/25 hover:shadow-xl hover:shadow-blue-600/30 border border-blue-700/50 cursor-pointer transition-all duration-200',
			dark: 'backdrop-blur-xl bg-blue-500/95 hover:bg-blue-600 text-white font-semibold shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/30 border border-blue-400/50 cursor-pointer transition-all duration-200',
		},
		secondary: {
			light: 'backdrop-blur-xl bg-white/5 hover:bg-white/20 text-gray-900 font-semibold shadow-lg shadow-gray-400/5 hover:shadow-lg hover:shadow-gray-400/15 border border-white/10 hover:border-white/30 cursor-pointer transition-all duration-200 drop-shadow-sm',
			dark: 'backdrop-blur-xl bg-gray-800/8 hover:bg-gray-700/30 text-white font-semibold shadow-lg shadow-gray-600/5 hover:shadow-lg hover:shadow-gray-600/15 border border-gray-600/10 hover:border-gray-600/30 cursor-pointer transition-all duration-200 drop-shadow-sm',
		},
		ghost: {
			light: 'backdrop-blur-xl bg-transparent hover:bg-white/15 text-gray-800 hover:text-gray-900 shadow-none hover:shadow-md hover:shadow-gray-400/10 border border-transparent hover:border-white/20 cursor-pointer transition-all duration-200 font-medium drop-shadow-sm',
			dark: 'backdrop-blur-xl bg-transparent hover:bg-gray-800/20 text-gray-100 hover:text-white shadow-none hover:shadow-md hover:shadow-gray-600/10 border border-transparent hover:border-gray-600/20 cursor-pointer transition-all duration-200 font-medium drop-shadow-sm',
		},
		link: {
			light: 'text-blue-600 hover:text-blue-700 font-medium hover:bg-blue-50/20 hover:shadow-sm hover:shadow-blue-500/10 cursor-pointer transition-all duration-200 drop-shadow-sm',
			dark: 'text-blue-400 hover:text-blue-300 font-medium hover:bg-blue-900/20 hover:shadow-sm hover:shadow-blue-400/10 cursor-pointer transition-all duration-200 drop-shadow-sm',
		},
		accent: {
			light: 'backdrop-blur-xl bg-gradient-to-r from-blue-500/10 to-purple-500/10 hover:from-blue-500/20 hover:to-purple-500/20 text-blue-600 hover:text-blue-700 font-semibold shadow-lg shadow-blue-500/10 hover:shadow-xl hover:shadow-blue-500/20 border border-blue-500/20 hover:border-blue-500/40 cursor-pointer transition-all duration-200 drop-shadow-sm',
			dark: 'backdrop-blur-xl bg-gradient-to-r from-blue-500/15 to-purple-500/15 hover:from-blue-500/25 hover:to-purple-500/25 text-blue-400 hover:text-blue-300 font-semibold shadow-lg shadow-blue-400/10 hover:shadow-xl hover:shadow-blue-400/20 border border-blue-400/20 hover:border-blue-400/40 cursor-pointer transition-all duration-200 drop-shadow-sm',
		},
		disabled: {
			light: 'backdrop-blur-xl bg-gray-300/60 text-gray-500 font-medium cursor-not-allowed opacity-60 shadow-sm border border-gray-300/50',
			dark: 'backdrop-blur-xl bg-gray-600/60 text-gray-400 font-medium cursor-not-allowed opacity-60 shadow-sm border border-gray-600/50',
		},
	},
} as const;

// Create theme context
const GlassThemeContext = createContext<GlassThemeConfig | undefined>(undefined);

// Theme provider component
interface GlassThemeProviderProps {
	children: ReactNode;
	defaultTheme?: ThemeMode;
	storageKey?: string;
}

export function GlassThemeProvider({ children, defaultTheme = 'light', storageKey = 'aditas-theme' }: GlassThemeProviderProps) {
	const [mode, setMode] = useState<ThemeMode>(defaultTheme);
	const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>('light');

	// Get system theme preference
	const getSystemTheme = (): ResolvedTheme => {
		if (typeof window !== 'undefined') {
			return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
		}
		return 'light';
	};

	// Initialize theme from cookies
	useEffect(() => {
		try {
			const stored = CookieManager.get(storageKey);
			if (stored && ['light', 'dark', 'system'].includes(stored)) {
				setMode(stored as ThemeMode);
			}
		} catch {
			// In a real app, you'd want to use the logger here
			// For now, silently fail to avoid dependency issues
		}
	}, [storageKey]);

	// Update resolved theme when mode changes or system preference changes
	useEffect(() => {
		const newResolvedTheme = mode === 'system' ? getSystemTheme() : mode;
		setResolvedTheme(newResolvedTheme);

		// Apply theme to document
		if (typeof document !== 'undefined') {
			const root = document.documentElement;
			root.classList.remove('light', 'dark');
			root.classList.add(newResolvedTheme);

			// Set data attribute for Tailwind CSS dark mode
			root.setAttribute('data-theme', newResolvedTheme);
		}

		// Save to cookies
		try {
			CookieManager.set(storageKey, mode, {
				expires: 365, // 1 year
				path: '/',
				secure: typeof window !== 'undefined' && window.location.protocol === 'https:',
				sameSite: 'lax'
			});
		} catch {
			// In a real app, you'd want to use the logger here
			// For now, silently fail to avoid dependency issues
		}
	}, [mode, storageKey]);

	// Listen for system theme changes
	useEffect(() => {
		if (mode !== 'system') return;

		const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
		const handleChange = () => {
			setResolvedTheme(getSystemTheme());
		};

		mediaQuery.addEventListener('change', handleChange);
		return () => mediaQuery.removeEventListener('change', handleChange);
	}, [mode]);

	const setTheme = (newTheme: ThemeMode) => {
		setMode(newTheme);
	};

	const toggleTheme = () => {
		if (mode === 'system') {
			setMode('light');
		} else if (mode === 'light') {
			setMode('dark');
		} else {
			setMode('system');
		}
	};

	const value: GlassThemeConfig = {
		mode,
		resolvedTheme,
		setTheme,
		toggleTheme,
	};

	return <GlassThemeContext.Provider value={value}>{children}</GlassThemeContext.Provider>;
}

// Hook to use glass theme
export function useGlassTheme() {
	const context = useContext(GlassThemeContext);
	if (context === undefined) {
		throw new Error('useGlassTheme must be used within a GlassThemeProvider');
	}
	return context;
}

// Safe hook that doesn't throw when used outside provider
export function useSafeGlassTheme() {
	const context = useContext(GlassThemeContext);
	if (context === undefined) {
		// Return fallback values when provider is not available
		return {
			mode: 'light' as ThemeMode,
			resolvedTheme: 'light' as ResolvedTheme,
			setTheme: () => {},
			toggleTheme: () => {},
		};
	}
	return context;
}

// Utility function to get glass class names
export function getGlassClass(variant: keyof typeof glassVariants, subVariant?: string, theme?: ResolvedTheme): string {
	const currentTheme = theme || 'light'; // Fallback if theme not provided
	const variantObj = glassVariants[variant];

	if (!variantObj) return '';

	if (subVariant && typeof variantObj === 'object' && subVariant in variantObj) {
		const subObj = (variantObj as Record<string, unknown>)[subVariant];
		if (typeof subObj === 'object' && subObj !== null && currentTheme in subObj) {
			return (subObj as Record<string, string>)[currentTheme];
		}
		return typeof subObj === 'string' ? subObj : '';
	}

	if (typeof variantObj === 'object' && currentTheme in variantObj) {
		return (variantObj as Record<string, string>)[currentTheme];
	}

	return '';
}

// Glass component utilities
export function useGlassClasses() {
	const { resolvedTheme } = useGlassTheme();

	return {
		// Layout components
		background: getGlassClass('background', undefined, resolvedTheme),
		card: getGlassClass('card', undefined, resolvedTheme),
		nav: getGlassClass('nav', 'main', resolvedTheme),
		header: getGlassClass('header', undefined, resolvedTheme),
		footer: getGlassClass('footer', undefined, resolvedTheme),
		sidebar: getGlassClass('sidebar', undefined, resolvedTheme),
		form: getGlassClass('form', undefined, resolvedTheme),

		// Interactive components
		input: getGlassClass('input', undefined, resolvedTheme),
		inputError: getGlassClass('input', 'error', resolvedTheme),
		dropdown: getGlassClass('dropdown', undefined, resolvedTheme),
		avatar: getGlassClass('avatar', undefined, resolvedTheme),

		// Button variants
		buttonPrimary: getGlassClass('button', 'primary', resolvedTheme),
		buttonSecondary: getGlassClass('button', 'secondary', resolvedTheme),
		buttonGhost: getGlassClass('button', 'ghost', resolvedTheme),
		buttonDisabled: getGlassClass('button', 'disabled', resolvedTheme),

		// Button-like variants (for links, spans, divs acting as buttons)
		buttonLikePrimary: getGlassClass('buttonLike', 'primary', resolvedTheme),
		buttonLikeSecondary: getGlassClass('buttonLike', 'secondary', resolvedTheme),
		buttonLikeGhost: getGlassClass('buttonLike', 'ghost', resolvedTheme),
		buttonLikeLink: getGlassClass('buttonLike', 'link', resolvedTheme),
		buttonLikeAccent: getGlassClass('buttonLike', 'disabled', resolvedTheme),
		buttonLikeDisabled: getGlassClass('buttonLike', 'disabled', resolvedTheme),

		// Text variants
		textPrimary: getGlassClass('text', 'primary', resolvedTheme),
		textSecondary: getGlassClass('text', 'secondary', resolvedTheme),
		textMuted: getGlassClass('text', 'muted', resolvedTheme),
		textLabel: getGlassClass('text', 'label', resolvedTheme),
		textPlaceholder: getGlassClass('text', 'placeholder', resolvedTheme),
		textHeading: getGlassClass('text', 'heading', resolvedTheme),
		textAccent: getGlassClass('text', 'accent', resolvedTheme),
		textError: getGlassClass('text', 'error', resolvedTheme),
		textErrorHeading: getGlassClass('text', 'errorHeading', resolvedTheme),
		textSuccess: getGlassClass('text', 'success', resolvedTheme),
		textInfo: getGlassClass('text', 'info', resolvedTheme),
		textInfoHeading: getGlassClass('text', 'infoHeading', resolvedTheme),

		// Badge variants
		badge: getGlassClass('badge', undefined, resolvedTheme),

		// Modal variants
		modalOverlay: getGlassClass('modal', 'overlay', resolvedTheme),
		modalContent: getGlassClass('modal', 'content', resolvedTheme),

		// Alert variants
		alertInfo: getGlassClass('alert', 'info', resolvedTheme),
		alertSuccess: getGlassClass('alert', 'success', resolvedTheme),
		alertWarning: getGlassClass('alert', 'warning', resolvedTheme),
		alertError: getGlassClass('alert', 'error', resolvedTheme),

		// Decorative elements
		orbs: glassVariants.orbs[resolvedTheme],
		divider: getGlassClass('divider', undefined, resolvedTheme),
		dividerText: getGlassClass('dividerText', undefined, resolvedTheme),
		separator: getGlassClass('separator', undefined, resolvedTheme),

		// Navigation specific variants
		navMain: getGlassClass('nav', 'main', resolvedTheme),
		navItem: getGlassClass('nav', 'item', resolvedTheme),
		navItemActive: getGlassClass('nav', 'itemActive', resolvedTheme),
		navDropdown: getGlassClass('nav', 'dropdown', resolvedTheme),
		avatarButton: getGlassClass('avatarButton', undefined, resolvedTheme),

		// Add to useGlassClasses return object
		// Form controls
		checkbox: getGlassClass('checkbox', undefined, resolvedTheme),
		radio: getGlassClass('radio', undefined, resolvedTheme),
		toggle: getGlassClass('toggle', undefined, resolvedTheme),
		select: getGlassClass('select', undefined, resolvedTheme),
		textarea: getGlassClass('textarea', undefined, resolvedTheme),

		// Layout
		section: getGlassClass('section', undefined, resolvedTheme),
		container: getGlassClass('container', undefined, resolvedTheme),
		panel: getGlassClass('panel', undefined, resolvedTheme),

		// Status indicators
		progressTrack: getGlassClass('progressBar', 'track', resolvedTheme),
		progressFill: getGlassClass('progressBar', 'fill', resolvedTheme),
		skeleton: getGlassClass('skeleton', undefined, resolvedTheme),
		spinner: getGlassClass('spinner', undefined, resolvedTheme),

		// Navigation
		// breadcrumb: getGlassClass('breadcrumb', undefined, resolvedTheme),
		// menu: getGlassClass('menu', undefined, resolvedTheme),
		// menuItem: getGlassClass('menuItem', undefined, resolvedTheme),

		// Interactive
		tooltip: getGlassClass('tooltip', undefined, resolvedTheme),
		popover: getGlassClass('popover', undefined, resolvedTheme),
		drawer: getGlassClass('drawer', undefined, resolvedTheme),

		// Data display
		table: getGlassClass('table', undefined, resolvedTheme),
		tableHeader: getGlassClass('tableHeader', undefined, resolvedTheme),
		tableRow: getGlassClass('tableRow', undefined, resolvedTheme),
		list: getGlassClass('list', undefined, resolvedTheme),
		listItem: getGlassClass('listItem', undefined, resolvedTheme),

		// Loading and feedback variants
		loadingSpinnerSmall: getGlassClass('loadingSpinner', 'small', resolvedTheme),
		loadingSpinnerMedium: getGlassClass('loadingSpinner', 'medium', resolvedTheme),
		loadingSpinnerLarge: getGlassClass('loadingSpinner', 'large', resolvedTheme),
		warningAlert: getGlassClass('warningAlert', undefined, resolvedTheme),
		warningText: getGlassClass('warningText', undefined, resolvedTheme),
		modalBackdrop: getGlassClass('modalBackdrop', undefined, resolvedTheme),
		errorBoundaryContainer: getGlassClass('errorBoundary', 'container', resolvedTheme),
		errorBoundaryCard: getGlassClass('errorBoundary', 'card', resolvedTheme),
		errorBoundaryBackground: getGlassClass('errorBoundary', 'background', resolvedTheme),
		imageBorder: getGlassClass('imageBorder', undefined, resolvedTheme),
		placeholderText: getGlassClass('placeholderText', undefined, resolvedTheme),
		themeToggleHover: getGlassClass('themeToggleHover', undefined, resolvedTheme),
		formSliderTrack: getGlassClass('slider', undefined, resolvedTheme),
	};
}

// Safe glass classes hook with fallbacks
export function useSafeGlassClasses() {
	const context = useContext(GlassThemeContext);

	// If no provider, return basic fallback classes
	if (context === undefined) {
		return {
			// Layout components
			background: 'bg-gradient-to-br from-blue-50 via-white to-indigo-50',
			card: 'backdrop-blur-xl bg-white/90 shadow-2xl shadow-gray-500/5',
			nav: 'backdrop-blur-lg bg-white/3 shadow-xl shadow-gray-500/6 border-b border-white/8',
			header: 'backdrop-blur-lg bg-white/85 shadow-md shadow-gray-500/5',
			footer: 'backdrop-blur-md bg-white/70 shadow-lg shadow-gray-500/5',
			sidebar: 'backdrop-blur-lg bg-white/85 shadow-xl shadow-gray-500/5',
			form: 'backdrop-blur-lg bg-white/85 shadow-xl rounded-2xl shadow-gray-500/5',

			// Interactive components
			input: 'backdrop-blur-sm bg-white/80 shadow-lg focus:bg-white/95 focus:shadow-xl focus:shadow-blue-500/10',
			inputError: 'ring-2 ring-red-500/60 bg-red-50/60 shadow-lg shadow-red-500/10',
			dropdown: 'backdrop-blur-xl bg-white/95 shadow-2xl rounded-xl shadow-gray-500/10',
			avatar: 'backdrop-blur-sm bg-white/60 shadow-lg shadow-gray-400/10 hover:shadow-xl hover:shadow-gray-400/15',

			// Button variants
			buttonPrimary: 'bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-lg shadow-blue-600/25 hover:shadow-xl hover:shadow-blue-600/30',
			buttonSecondary:
				'backdrop-blur-sm bg-white/60 hover:bg-white/80 text-gray-900 font-semibold shadow-md shadow-gray-400/10 hover:shadow-lg hover:shadow-gray-400/15',
			buttonGhost: 'backdrop-blur-sm bg-transparent hover:bg-white/20 text-gray-700 hover:text-gray-900 hover:shadow-md hover:shadow-gray-400/10',
			buttonDisabled: 'bg-gradient-to-r from-gray-300 to-gray-400 cursor-not-allowed opacity-60 font-semibold shadow-sm',

			// Button text variants
			buttonTextPrimary: 'text-white',
			buttonTextSecondary: 'text-gray-900',
			buttonTextGhost: 'text-gray-700 hover:text-gray-900',
			buttonTextDisabled: 'text-gray-500',

			// Text variants
			textPrimary: 'text-gray-900',
			textSecondary: 'text-gray-700',
			textMuted: 'text-gray-500',
			textLabel: 'text-gray-800 font-medium',
			textPlaceholder: 'placeholder-gray-400',
			textHeading: 'text-gray-900 font-bold',
			textAccent: 'text-blue-600 font-semibold',

		// Badge variants
		badge: 'bg-gray-100 text-gray-900 border border-gray-200',

		// Modal variants
			modalOverlay: 'backdrop-blur-sm bg-black/20',
			modalContent: 'backdrop-blur-xl bg-white/95 shadow-2xl rounded-3xl shadow-gray-500/10',

			// Alert variants
			alertInfo: 'backdrop-blur-md bg-blue-50/90 shadow-lg shadow-blue-500/10 text-blue-800',
			alertSuccess: 'backdrop-blur-md bg-green-50/90 shadow-lg shadow-green-500/10 text-green-800',
			alertWarning: 'backdrop-blur-md bg-yellow-50/90 shadow-lg shadow-yellow-500/10 text-yellow-800',
			alertError: 'backdrop-blur-md bg-red-50/90 shadow-lg shadow-red-500/10 text-red-800',

			// Decorative elements
			orbs: ['bg-gradient-to-br from-blue-500/20 to-purple-500/20', 'bg-gradient-to-tr from-teal-500/20 to-blue-500/20'],
			divider: 'shadow-sm shadow-gray-400/10 bg-gradient-to-r from-transparent via-gray-300/10 to-transparent h-px',
			dividerText: 'bg-white/95 text-gray-600 shadow-sm shadow-gray-400/5',
			separator: 'shadow-sm shadow-gray-400/5 bg-gradient-to-r from-transparent via-gray-300/30 to-transparent h-px my-2',

			// Navigation specific variants
			navMain: 'backdrop-blur-lg bg-white/3 shadow-xl shadow-gray-500/5',
			navItem: 'hover:bg-white/40 hover:shadow-md hover:shadow-gray-400/10 transition-all duration-200',
			navItemActive: 'bg-blue-50/10 shadow-md shadow-blue-500/15 text-blue-600',
			navDropdown: 'backdrop-blur-xl bg-white/95 shadow-2xl shadow-gray-500/10',
			avatarButton:
				'backdrop-blur-sm bg-blue-100/20 hover:bg-blue-100/30 shadow-lg shadow-blue-500/15 hover:shadow-xl hover:shadow-blue-500/20',

			// New loading and feedback variants (fallbacks)
			loadingSpinnerSmall: 'w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin',
			loadingSpinnerMedium: 'w-8 h-8 border-b-2 border-blue-500 rounded-full animate-spin',
			loadingSpinnerLarge: 'w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin',
			warningAlert: 'backdrop-blur-md bg-orange-50/90 shadow-lg shadow-orange-500/10 text-orange-800 border border-orange-200/50',
			warningText: 'text-orange-600',
			modalBackdrop: 'fixed inset-0 backdrop-blur-sm bg-black/20 z-50',
			errorBoundaryContainer: 'min-h-screen flex items-center justify-center p-4',
			errorBoundaryCard: 'backdrop-blur-xl bg-white/95 shadow-2xl rounded-2xl p-8 max-w-lg w-full border border-red-200/50',
			errorBoundaryBackground: 'bg-red-50/80',
			imageBorder: 'border border-gray-200/60 rounded-lg',
			placeholderText: 'placeholder:text-gray-400',
			themeToggleHover: 'hover:bg-white/40 hover:shadow-md hover:shadow-blue-500/10',
		};
	}

	// If provider is available, use the regular hook
	const { resolvedTheme } = context;

	return {
		// Layout components
		background: getGlassClass('background', undefined, resolvedTheme),
		card: getGlassClass('card', undefined, resolvedTheme),
		nav: getGlassClass('nav', 'main', resolvedTheme),
		header: getGlassClass('header', undefined, resolvedTheme),
		footer: getGlassClass('footer', undefined, resolvedTheme),
		sidebar: getGlassClass('sidebar', undefined, resolvedTheme),
		form: getGlassClass('form', undefined, resolvedTheme),

		// Interactive components
		input: getGlassClass('input', undefined, resolvedTheme),
		inputError: getGlassClass('input', 'error', resolvedTheme),
		dropdown: getGlassClass('dropdown', undefined, resolvedTheme),
		avatar: getGlassClass('avatar', undefined, resolvedTheme),

		// Button variants
		buttonPrimary: getGlassClass('button', 'primary', resolvedTheme),
		buttonSecondary: getGlassClass('button', 'secondary', resolvedTheme),
		buttonGhost: getGlassClass('button', 'ghost', resolvedTheme),
		buttonDisabled: getGlassClass('button', 'disabled', resolvedTheme),

		// Button-like variants (for links, spans, divs acting as buttons)
		buttonLikePrimary: getGlassClass('buttonLike', 'primary', resolvedTheme),
		buttonLikeSecondary: getGlassClass('buttonLike', 'secondary', resolvedTheme),
		buttonLikeGhost: getGlassClass('buttonLike', 'ghost', resolvedTheme),
		buttonLikeLink: getGlassClass('buttonLike', 'link', resolvedTheme),
		buttonLikeAccent: getGlassClass('buttonLike', 'disabled', resolvedTheme),
		buttonLikeDisabled: getGlassClass('buttonLike', 'disabled', resolvedTheme),

		// Text variants
		textPrimary: getGlassClass('text', 'primary', resolvedTheme),
		textSecondary: getGlassClass('text', 'secondary', resolvedTheme),
		textMuted: getGlassClass('text', 'muted', resolvedTheme),
		textLabel: getGlassClass('text', 'label', resolvedTheme),
		textPlaceholder: getGlassClass('text', 'placeholder', resolvedTheme),
		textHeading: getGlassClass('text', 'heading', resolvedTheme),
		textAccent: getGlassClass('text', 'accent', resolvedTheme),
		textError: getGlassClass('text', 'error', resolvedTheme),
		textErrorHeading: getGlassClass('text', 'errorHeading', resolvedTheme),
		textSuccess: getGlassClass('text', 'success', resolvedTheme),
		textInfo: getGlassClass('text', 'info', resolvedTheme),
		textInfoHeading: getGlassClass('text', 'infoHeading', resolvedTheme),

		// Badge variants
		badge: getGlassClass('badge', undefined, resolvedTheme),

		// Modal variants
		modalOverlay: getGlassClass('modal', 'overlay', resolvedTheme),
		modalContent: getGlassClass('modal', 'content', resolvedTheme),

		// Alert variants
		alertInfo: getGlassClass('alert', 'info', resolvedTheme),
		alertSuccess: getGlassClass('alert', 'success', resolvedTheme),
		alertWarning: getGlassClass('alert', 'warning', resolvedTheme),
		alertError: getGlassClass('alert', 'error', resolvedTheme),

		// Decorative elements
		orbs: glassVariants.orbs[resolvedTheme],
		divider: getGlassClass('divider', undefined, resolvedTheme),
		dividerText: getGlassClass('dividerText', undefined, resolvedTheme),
		separator: getGlassClass('separator', undefined, resolvedTheme),

		// Navigation specific variants
		navMain: getGlassClass('nav', 'main', resolvedTheme),
		navItem: getGlassClass('nav', 'item', resolvedTheme),
		navItemActive: getGlassClass('nav', 'itemActive', resolvedTheme),
		navDropdown: getGlassClass('nav', 'dropdown', resolvedTheme),
		avatarButton: getGlassClass('avatarButton', undefined, resolvedTheme),

		// New loading and feedback variants
		loadingSpinnerSmall: getGlassClass('loadingSpinner', 'small', resolvedTheme),
		loadingSpinnerMedium: getGlassClass('loadingSpinner', 'medium', resolvedTheme),
		loadingSpinnerLarge: getGlassClass('loadingSpinner', 'large', resolvedTheme),
		warningAlert: getGlassClass('warningAlert', undefined, resolvedTheme),
		warningText: getGlassClass('warningText', undefined, resolvedTheme),
		modalBackdrop: getGlassClass('modalBackdrop', undefined, resolvedTheme),
		errorBoundaryContainer: getGlassClass('errorBoundary', 'container', resolvedTheme),
		errorBoundaryCard: getGlassClass('errorBoundary', 'card', resolvedTheme),
		errorBoundaryBackground: getGlassClass('errorBoundary', 'background', resolvedTheme),
		imageBorder: getGlassClass('imageBorder', undefined, resolvedTheme),
		placeholderText: getGlassClass('placeholderText', undefined, resolvedTheme),
		themeToggleHover: getGlassClass('themeToggleHover', undefined, resolvedTheme),
	};
}

// Simple theme toggle button component (without icons)
export function SimpleThemeToggle({ className = '' }: { className?: string }) {
	const context = useContext(GlassThemeContext);
	const resolvedTheme = context?.resolvedTheme || 'light';

	const getLabel = () => {
		if (!context) return 'Switch Theme';
		switch (context.mode) {
			case 'light':
				return 'Dark Mode';
			case 'dark':
				return 'Light Mode';
			case 'system':
				return 'System Theme';
			default:
				return 'Switch Theme';
		}
	};

	const handleToggle = () => {
		if (context) {
			context.toggleTheme();
		}
	};

	// Use theme-aware classes
	const hoverClass = getGlassClass('nav', 'item', resolvedTheme);
	const textClass = getGlassClass('text', 'primary', resolvedTheme);

	return (
		<button
			onClick={handleToggle}
			className={`w-full text-left px-4 py-2 rounded-lg transition-all duration-200 ${hoverClass} ${textClass} ${className}`}>
			<span className='flex items-center gap-2'>
				<span className='text-lg'>{context?.mode === 'light' ? '☀️' : context?.mode === 'dark' ? '🌙' : '🔄'}</span>
				{getLabel()}
			</span>
		</button>
	);
}

// Theme toggle button component
export function ThemeToggle({ className = '' }: { className?: string }) {
	const { mode, toggleTheme } = useGlassTheme();
	const { buttonSecondary } = useGlassClasses();

	const getIcon = () => {
		switch (mode) {
			case 'light':
				return '☀️';
			case 'dark':
				return '🌙';
			case 'system':
				return '🔄';
			default:
				return '☀️';
		}
	};

	const getLabel = () => {
		switch (mode) {
			case 'light':
				return 'Light mode';
			case 'dark':
				return 'Dark mode';
			case 'system':
				return 'System mode';
			default:
				return 'Light mode';
		}
	};

	return (
		<button
			onClick={toggleTheme}
			className={`p-2 rounded-xl transition-all duration-300 hover:scale-105 ${buttonSecondary} ${className}`}
			title={getLabel()}
			aria-label={getLabel()}>
			<span className='text-lg'>{getIcon()}</span>
		</button>
	);
}
